'use client';

import { useState, useMemo, useEffect } from 'react';
import { Card, CardContent } from './card';
import { Input } from './input';
import { Button } from './button';
import { cn } from '@/lib/utils';
import { type FAQ, type FAQCategory, categories, allTags } from '@/lib/faqs';

interface FAQPageProps {
  faqs: FAQ[];
}

const ITEMS_PER_PAGE = 20; // Initial load and increment

export function FAQPage({ faqs }: FAQPageProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<Set<FAQCategory>>(new Set());
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
  const [openIndex, setOpenIndex] = useState<string | null>(null);
  const [tagsAccordionOpen, setTagsAccordionOpen] = useState(false);
  const [displayedCount, setDisplayedCount] = useState(ITEMS_PER_PAGE);

  const toggleCategory = (category: FAQCategory) => {
    setSelectedCategories(prev => {
      const next = new Set(prev);

      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }

      return next;
    });
  };

  const toggleTag = (tag: string) => {
    setSelectedTags(prev => {
      const next = new Set(prev);

      if (next.has(tag)) {
        next.delete(tag);
      } else {
        next.add(tag);
      }

      return next;
    });
  };

  const toggleAccordion = (id: string) => {
    setOpenIndex(openIndex === id ? null : id);
  };

  // Filter on full dataset first (before pagination)
  const filteredFAQs = useMemo(() => {
    return faqs.filter(faq => {
      // Search filter
      const matchesSearch = searchQuery === '' || 
        faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchQuery.toLowerCase());

      // Category filter (AND logic - must match if categories are selected)
      const matchesCategory = selectedCategories.size === 0 || 
        selectedCategories.has(faq.category);

      // Tag filter (OR logic - must match any selected tag if tags are selected)
      const matchesTag = selectedTags.size === 0 || 
        faq.tags.some(tag => selectedTags.has(tag));

      return matchesSearch && matchesCategory && matchesTag;
    });
  }, [faqs, searchQuery, selectedCategories, selectedTags]);

  // Reset displayed count when filters change
  useEffect(() => {
    setDisplayedCount(ITEMS_PER_PAGE);
  }, [searchQuery, selectedCategories, selectedTags]);

  // Get displayed FAQs (lazy loaded subset)
  const displayedFAQs = useMemo(() => {
    return filteredFAQs.slice(0, displayedCount);
  }, [filteredFAQs, displayedCount]);

  const hasMore = displayedCount < filteredFAQs.length;

  const loadMore = () => {
    setDisplayedCount(prev => Math.min(prev + ITEMS_PER_PAGE, filteredFAQs.length));
  };

  // Split displayed FAQs into two columns for desktop
  const leftColumn = displayedFAQs.filter((_, index) => index % 2 === 0);
  const rightColumn = displayedFAQs.filter((_, index) => index % 2 === 1);

  return (
    <div className="w-full">
      {/* Search and Filters Section */}
      <div className="mb-12 space-y-6">
        {/* Search Input */}
        <div className="relative">
          <Input
            type="text"
            placeholder="Search FAQs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full"
          />
          <svg
            className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[rgb(var(--foreground-secondary))] pointer-events-none"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>

        {/* Category Filters */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-[rgb(var(--foreground))]">
            Categories
          </h3>
          <div className="flex flex-wrap gap-2">
            {categories.map(category => {
              const isSelected = selectedCategories.has(category);

              return (
                <Button
                  key={category}
                  variant={isSelected ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={() => toggleCategory(category)}
                  className={cn(
                    'transition-all',
                    isSelected
                      ? 'bg-[rgb(var(--primary))] text-white hover:bg-[rgb(var(--primary-hover))]'
                      : 'bg-[rgb(var(--surface))] text-[rgb(var(--foreground))] border border-[rgb(var(--surface-border))] hover:bg-[rgb(var(--surface-hover))]'
                  )}
                >
                  {category}
                </Button>
              );
            })}
          </div>
        </div>

        {/* Tag Filters - Accordion */}
        <div className="space-y-3">
          <button
            onClick={() => setTagsAccordionOpen(!tagsAccordionOpen)}
            className="w-full flex items-center justify-between group"
          >
            <h3 className="text-sm font-semibold text-[rgb(var(--foreground))]">
              Tags {selectedTags.size > 0 && (
                <span className="ml-2 text-xs text-[rgb(var(--primary))]">
                  ({selectedTags.size} selected)
                </span>
              )}
            </h3>
            <svg
              className={cn(
                'w-5 h-5 text-[rgb(var(--foreground-secondary))] transition-transform duration-300',
                tagsAccordionOpen && 'rotate-180'
              )}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
          <div
            className={cn(
              'overflow-hidden transition-all duration-300 ease-in-out',
              tagsAccordionOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
            )}
          >
            <div className="flex flex-wrap gap-2 pt-2">
              {allTags.map(tag => {
                const isSelected = selectedTags.has(tag);

                return (
                  <button
                    key={tag}
                    onClick={() => toggleTag(tag)}
                    className={cn(
                      'px-3 py-1.5 rounded-lg text-sm font-medium transition-all',
                      isSelected
                        ? 'bg-[rgb(var(--primary))] text-white hover:bg-[rgb(var(--primary-hover))]'
                        : 'bg-[rgb(var(--surface))] text-[rgb(var(--foreground))] border border-[rgb(var(--surface-border))] hover:bg-[rgb(var(--surface-hover))]'
                    )}
                  >
                    {tag}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Active Filters Summary */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-[rgb(var(--foreground-secondary))]">
            {filteredFAQs.length === faqs.length ? (
              <>Showing {displayedFAQs.length} of {filteredFAQs.length} FAQs</>
            ) : (
              <>Showing {displayedFAQs.length} of {filteredFAQs.length} filtered FAQs (from {faqs.length} total)</>
            )}
          </span>
          {(selectedCategories.size > 0 || selectedTags.size > 0 || searchQuery) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearchQuery('');
                setSelectedCategories(new Set());
                setSelectedTags(new Set());
              }}
              className="text-xs text-[rgb(var(--foreground-secondary))] hover:text-[rgb(var(--foreground))]"
            >
              Clear all filters
            </Button>
          )}
        </div>
      </div>

      {/* FAQs Display */}
      {filteredFAQs.length === 0 ? (
        <Card variant="elevated" className="p-12 text-center">
          <CardContent className="p-0">
            <svg
              className="w-16 h-16 mx-auto mb-4 text-[rgb(var(--foreground-secondary))]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h3 className="text-xl font-semibold text-[rgb(var(--foreground))] mb-2">
              No FAQs found
            </h3>
            <p className="text-[rgb(var(--foreground-secondary))]">
              Try adjusting your search or filters to find what you&apos;re looking for.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Left Column */}
          <Card variant="elevated" className="p-6">
            <CardContent className="p-0 space-y-4">
              {leftColumn.map((faq) => {
                const isOpen = openIndex === faq.id;

                return (
                  <div
                    key={faq.id}
                    className="border-b border-[rgb(var(--surface-border))] last:border-0 pb-4 last:pb-0"
                  >
                    <button
                      onClick={() => toggleAccordion(faq.id)}
                      className="w-full flex items-center justify-between gap-4 text-left group"
                    >
                      <h3 className="text-base font-semibold text-[rgb(var(--foreground))] group-hover:text-[rgb(var(--primary))] transition-colors pr-8">
                        {faq.question}
                      </h3>
                      <svg
                        className={cn(
                          'w-5 h-5 text-[rgb(var(--foreground-secondary))] flex-shrink-0 transition-transform duration-300',
                          isOpen && 'rotate-180'
                        )}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                    <div
                      className={cn(
                        'overflow-hidden transition-all duration-300 ease-in-out',
                        isOpen ? 'max-h-96 mt-3' : 'max-h-0'
                      )}
                    >
                      <p className="text-sm text-[rgb(var(--foreground-secondary))] leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Right Column */}
          <Card variant="elevated" className="p-6">
            <CardContent className="p-0 space-y-4">
              {rightColumn.map((faq) => {
                const isOpen = openIndex === faq.id;

                return (
                  <div
                    key={faq.id}
                    className="border-b border-[rgb(var(--surface-border))] last:border-0 pb-4 last:pb-0"
                  >
                    <button
                      onClick={() => toggleAccordion(faq.id)}
                      className="w-full flex items-center justify-between gap-4 text-left group"
                    >
                      <h3 className="text-base font-semibold text-[rgb(var(--foreground))] group-hover:text-[rgb(var(--primary))] transition-colors pr-8">
                        {faq.question}
                      </h3>
                      <svg
                        className={cn(
                          'w-5 h-5 text-[rgb(var(--foreground-secondary))] flex-shrink-0 transition-transform duration-300',
                          isOpen && 'rotate-180'
                        )}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                    <div
                      className={cn(
                        'overflow-hidden transition-all duration-300 ease-in-out',
                        isOpen ? 'max-h-96 mt-3' : 'max-h-0'
                      )}
                    >
                      <p className="text-sm text-[rgb(var(--foreground-secondary))] leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Load More Button */}
      {hasMore && (
        <div className="mt-8 text-center">
          <Button
            variant="secondary"
            onClick={loadMore}
            className="bg-[rgb(var(--surface))] text-[rgb(var(--foreground))] border border-[rgb(var(--surface-border))] hover:bg-[rgb(var(--surface-hover))]"
          >
            Load More FAQs ({filteredFAQs.length - displayedCount} remaining)
          </Button>
        </div>
      )}
    </div>
  );
}

